import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { PartyFormValues } from '../../Types/partyTypes';
import { validatePartyFormValues } from '../../Service/partiesMapper';

interface PartyFormProps {
  mode: 'create' | 'edit';
  initialValues: PartyFormValues;
  isSubmitting: boolean;
  submitErrorMessage?: string | null;
  isSuccess?: boolean;
  onSubmit: (values: PartyFormValues) => Promise<void>;
  onCancel: () => void;
}

type PartyFieldErrors = Partial<Record<keyof PartyFormValues, string>>;

const getFieldError = (
  fieldErrors: PartyFieldErrors,
  fieldName: keyof PartyFormValues
): { error: boolean; helperText: string | undefined } => ({
  error: Boolean(fieldErrors[fieldName]),
  helperText: fieldErrors[fieldName],
});

const PartyForm = ({
  mode,
  initialValues,
  isSubmitting,
  submitErrorMessage,
  isSuccess,
  onSubmit,
  onCancel,
}: PartyFormProps) => {
  const [formValues, setFormValues] = useState<PartyFormValues>(initialValues);
  const [fieldErrors, setFieldErrors] = useState<PartyFieldErrors>({});

  useEffect(() => {
    setFormValues(initialValues);
    setFieldErrors({});
  }, [initialValues]);

  const title = mode === 'create' ? 'Dodaj stronę' : 'Edytuj stronę';
  const successMessage =
    mode === 'create'
      ? 'Strona została zapisana. Trwa przekierowanie do listy.'
      : 'Zmiany zostały zapisane. Trwa przekierowanie do listy.';

  const isOrganization = formValues.partyType === 'organization';

  const commonFields = useMemo(
    () => (
      <>
        <TextField
          label="E-mail"
          value={formValues.email}
          onChange={(event) => setFormValues((prev) => ({ ...prev, email: event.target.value }))}
          {...getFieldError(fieldErrors, 'email')}
          fullWidth
        />
        <TextField
          label="Telefon"
          value={formValues.phone}
          onChange={(event) => setFormValues((prev) => ({ ...prev, phone: event.target.value }))}
          {...getFieldError(fieldErrors, 'phone')}
          fullWidth
        />
        <TextField
          label="Ulica"
          value={formValues.street}
          onChange={(event) => setFormValues((prev) => ({ ...prev, street: event.target.value }))}
          {...getFieldError(fieldErrors, 'street')}
          fullWidth
        />
        <TextField
          label="Miasto"
          value={formValues.city}
          onChange={(event) => setFormValues((prev) => ({ ...prev, city: event.target.value }))}
          {...getFieldError(fieldErrors, 'city')}
          fullWidth
        />
        <TextField
          label="Kod pocztowy"
          value={formValues.postalCode}
          onChange={(event) => setFormValues((prev) => ({ ...prev, postalCode: event.target.value }))}
          {...getFieldError(fieldErrors, 'postalCode')}
          fullWidth
        />
        <TextField
          label="Kod kraju"
          value={formValues.countryCode}
          onChange={(event) => setFormValues((prev) => ({ ...prev, countryCode: event.target.value }))}
          {...getFieldError(fieldErrors, 'countryCode')}
          fullWidth
        />
      </>
    ),
    [fieldErrors, formValues]
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const validationErrors = validatePartyFormValues(formValues);
    if (validationErrors) {
      const normalizedErrors = Object.entries(validationErrors).reduce<PartyFieldErrors>(
        (accumulator, [fieldName, messages]) => {
          if (messages.length > 0) {
            accumulator[fieldName as keyof PartyFormValues] = messages[0];
          }

          return accumulator;
        },
        {}
      );

      setFieldErrors(normalizedErrors);
      return;
    }

    setFieldErrors({});
    await onSubmit(formValues);
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={2}>
        <Typography variant="h4">{title}</Typography>

        <FormControl fullWidth>
          <InputLabel id="party-type-label">Typ strony</InputLabel>
          <Select
            labelId="party-type-label"
            label="Typ strony"
            value={formValues.partyType}
            onChange={(event) =>
              setFormValues((prev) => ({ ...prev, partyType: event.target.value as PartyFormValues['partyType'] }))
            }
          >
            <MenuItem value="organization">Organizacja</MenuItem>
            <MenuItem value="individual">Osoba fizyczna</MenuItem>
          </Select>
        </FormControl>

        {isOrganization ? (
          <>
            <TextField
              label="Nazwa organizacji"
              value={formValues.organizationName}
              onChange={(event) =>
                setFormValues((prev) => ({ ...prev, organizationName: event.target.value }))
              }
              {...getFieldError(fieldErrors, 'organizationName')}
              fullWidth
            />
            <TextField
              label="NIP"
              value={formValues.taxId}
              onChange={(event) => setFormValues((prev) => ({ ...prev, taxId: event.target.value }))}
              {...getFieldError(fieldErrors, 'taxId')}
              fullWidth
            />
            <TextField
              label="REGON"
              value={formValues.regon}
              onChange={(event) => setFormValues((prev) => ({ ...prev, regon: event.target.value }))}
              {...getFieldError(fieldErrors, 'regon')}
              fullWidth
            />
          </>
        ) : (
          <>
            <TextField
              label="Imię"
              value={formValues.firstName}
              onChange={(event) => setFormValues((prev) => ({ ...prev, firstName: event.target.value }))}
              {...getFieldError(fieldErrors, 'firstName')}
              fullWidth
            />
            <TextField
              label="Nazwisko"
              value={formValues.lastName}
              onChange={(event) => setFormValues((prev) => ({ ...prev, lastName: event.target.value }))}
              {...getFieldError(fieldErrors, 'lastName')}
              fullWidth
            />
          </>
        )}

        {commonFields}

        {submitErrorMessage && <Alert severity="error">{submitErrorMessage}</Alert>}
        {isSuccess && <Alert severity="success">{successMessage}</Alert>}

        <Stack direction="row" spacing={2}>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? 'Zapisywanie...' : mode === 'create' ? 'Utwórz' : 'Zapisz'}
          </Button>
          <Button type="button" variant="outlined" onClick={onCancel} disabled={isSubmitting}>
            Powrót
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default PartyForm;

